/**
 * First-party analytics tracker — client half of POST /api/track.
 *
 * Design (see plan.md PR A1 + REPLICATION-GUIDE-crm-and-monitoring.md §B1.2):
 *
 * - Every network call uses `fetch(..., { keepalive: true })` so a beacon in
 *   flight survives page unload (equivalent to `navigator.sendBeacon` but
 *   with headers we can control).
 * - Every error is swallowed. Analytics MUST NOT break the app.
 * - Pageview on route change — guard against duplicate fires with a
 *   `lastPath` module-level ref.
 * - Engaged-time heartbeat: 15s `setInterval` that only fires while
 *   `document.visibilityState === "visible"`. A `visibilitychange` listener
 *   resets the clock so hidden time doesn't count.
 * - Exported `track(name, props?)` for custom events (login, register, ...).
 *
 * The tracker itself owns no React state — it's a pair of module-level
 * functions plus one `useTracker()` hook that a single App-shell component
 * mounts once. Cross-origin cookieless callers (marketing site) receive the
 * server-issued sessionId in the response body and persist it in
 * localStorage as `cogslog_analytics_sid` — the tracker echoes it on future
 * beacons via the `sid` field.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getToken } from './keycloak';

// Read the persisted session id (only relevant for cookieless / cross-origin
// callers — the app.cogslog.com case relies on the httpOnly cookie the server
// sets, and this helper returns undefined there because we never write here
// from that origin).
const SID_STORAGE_KEY = 'cogslog_analytics_sid';

/** Safely read the persisted session id from localStorage. */
function readStoredSid() {
  try {
    return window.localStorage.getItem(SID_STORAGE_KEY) || undefined;
  } catch (_e) {
    return undefined;
  }
}

/** Safely persist a new session id (fire-and-forget; storage may be blocked). */
function writeStoredSid(sid) {
  if (!sid) return;
  try {
    window.localStorage.setItem(SID_STORAGE_KEY, sid);
  } catch (_e) {
    /* ignore quota / private-mode / storage-blocked failures */
  }
}

/**
 * Send one beacon to `/api/track`. Never throws. Never awaits.
 *
 * @param {object} payload  the `{type, path, title, ...}` body — sid and
 *                          screen are added here.
 */
function beacon(payload) {
  try {
    const body = {
      ...payload,
      sid: payload.sid || readStoredSid(),
      screen: payload.screen || `${window.innerWidth}x${window.innerHeight}`,
      language: payload.language || (navigator.language || undefined),
    };
    // Attach the Keycloak bearer token when the user is signed in so /api/track
    // can identify them (server side is best-effort — anonymous callers still
    // succeed with a NULL user_id). Fetching the token is safe here because
    // getToken() returns undefined when not signed in.
    const headers = { 'Content-Type': 'application/json' };
    try {
      const token = getToken && getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch (_e) {
      /* keycloak module unavailable in tests — proceed without a token */
    }
    fetch('/api/track', {
      method: 'POST',
      headers,
      credentials: 'include',
      keepalive: true,
      // Send only the origin as the Referer, never the full referring URL.
      // Tracking covers the auth pages, and several carry a secret in the
      // query string (/reset-password?token=…, /verify-email?token=…). The
      // browser's default same-origin policy would put that token in the
      // Referer header, which the server persists on the session row.
      // `path` in the body still carries the pathname, so nothing useful is
      // lost. The server strips query strings too (analytics_track.py::
      // _strip_query) — this is the client-side half of the same defence.
      referrerPolicy: 'strict-origin',
      body: JSON.stringify(body),
    })
      .then((res) => {
        // Persist server-issued sid for the cookieless (cross-origin) case.
        // Same-origin cookies survive on their own; this is idempotent there.
        if (res.ok) {
          res
            .json()
            .then((json) => {
              if (json && json.sessionId) writeStoredSid(json.sessionId);
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        /* swallow — analytics never breaks the page */
      });
  } catch (_e) {
    /* swallow — never propagate */
  }
}

/**
 * Fire a custom event beacon. Safe to call from anywhere in the app —
 * login handlers, form submissions, feature-flag toggles, etc.
 *
 * @param {string} name  short event identifier (≤120 chars — server clamps)
 * @param {object} [props]  optional key/value payload (must be plain object)
 */
export function track(name, props) {
  beacon({
    type: 'event',
    name,
    path: window.location.pathname,
    title: document.title,
    props: props && typeof props === 'object' && !Array.isArray(props) ? props : undefined,
  });
}

/**
 * Fire a pageview beacon. Called by `useTracker()` on every route change
 * (deduped internally). Exported so a component can force one after
 * programmatic navigation that doesn't change the URL.
 */
export function sendPageview(path) {
  beacon({
    type: 'pageview',
    path: path || window.location.pathname,
    title: document.title,
  });
}

/**
 * React hook — mount once at the App shell. Wires:
 *   1. Pageview on every route change (dedup via lastPath ref).
 *   2. 15s visibility-gated engaged-time heartbeat, sending
 *      `{ type: "ping", seconds: <delta> }`. Hidden time doesn't count.
 *
 * Not exported as a component to keep the boundary a hook — the mount site
 * chooses when in the tree the effect lives.
 */
export function useTracker() {
  const location = useLocation();
  const lastPathRef = useRef(null);
  const lastPingAtRef = useRef(Date.now());

  // Pageview on route change (dedup).
  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;
    sendPageview(location.pathname);
  }, [location.pathname, location.search]);

  // Engaged-time heartbeat — only counts visible time.
  useEffect(() => {
    // Reset the clock whenever visibility flips — hidden intervals don't count.
    const onVisibilityChange = () => {
      lastPingAtRef.current = Date.now();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const tick = () => {
      if (document.visibilityState !== 'visible') {
        // Hidden — reset ref so the next visible tick starts fresh.
        lastPingAtRef.current = Date.now();
        return;
      }
      const now = Date.now();
      const delta = Math.round((now - lastPingAtRef.current) / 1000);
      lastPingAtRef.current = now;
      if (delta > 0) {
        beacon({ type: 'ping', seconds: delta, path: window.location.pathname });
      }
    };
    const id = window.setInterval(tick, 15000);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
